import type { Control, FieldValues } from 'react-hook-form';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import { DynamicFormRequiredType } from '../../dynamic-form/dynamic-form.enums';
import OdinController, { type OdinControllerProps } from '../../dynamic-form/odin-controller.component';
import ErrorLabel from '../../labels/error-label';
import FieldLabel from '../../labels/field-label';
import { PaginatedCombobox, type PaginatedComboboxProps } from './paginated-combobox';
import { PaginatedMultiCombobox, type PaginatedMultiComboboxProps } from './paginated-multi-combobox';
import { StaticCombobox, type StaticComboboxProps } from './static-combobox';
import { StaticMultiCombobox, type StaticMultiComboboxProps } from './static-multi-combobox';

type DistributiveOmit<T, K extends keyof never> = T extends unknown ? Omit<T, K> : never;

type StaticProps<T> = {
  mode: 'static';
  onValueChange?: (value: T | null) => void;
} & DistributiveOmit<StaticComboboxProps<T>, 'value' | 'onValueChange' | 'required'>;
type StaticMultiProps<T> = {
  mode: 'static-multi';
  onValueChange?: (value: T[]) => void;
} & DistributiveOmit<StaticMultiComboboxProps<T>, 'value' | 'onValueChange' | 'required'>;
type PaginatedProps<T> = {
  mode: 'paginated';
  onValueChange?: (value: T | null) => void;
} & DistributiveOmit<PaginatedComboboxProps<T>, 'value' | 'onValueChange' | 'required'>;
type PaginatedMultiProps<T> = {
  mode: 'paginated-multi';
  onValueChange?: (value: T[]) => void;
} & DistributiveOmit<PaginatedMultiComboboxProps<T>, 'value' | 'onValueChange' | 'required'>;

interface FormProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: string;
  defaultValue?: unknown;
  /** Optional field label rendered above the combobox. Associates with the input via `id` when one is set. */
  label?: string;
  readOnly?: boolean;
  required?: DynamicFormRequiredType;
}

export type HfComboboxProps<T, TFieldValues extends FieldValues = FieldValues> = FormProps<TFieldValues> &
  (StaticProps<T> | StaticMultiProps<T> | PaginatedProps<T> | PaginatedMultiProps<T>);

type ControllerRender<T> = OdinControllerProps<T>['render'];

export const HfCombobox = <T, TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  defaultValue,
  label,
  readOnly = false,
  required = DynamicFormRequiredType.False,
  ...modeProps
}: HfComboboxProps<T, TFieldValues>) => {
  const render: ControllerRender<T | T[] | null> = ({ field, fieldState: { error } }) => {
    const ariaInvalid = error !== undefined ? true : modeProps['aria-invalid'];
    // `name` doubles as the input id / label `htmlFor`, with `id` an optional override.
    const identifier = modeProps.id ? String(modeProps.id) : name;

    const body = (() => {
      switch (modeProps.mode) {
        case 'static': {
          const { mode: _, onValueChange: __, ...rest } = modeProps;
          return (
            <StaticCombobox
              {...rest}
              id={identifier}
              value={(field.value as T | null) ?? null}
              onValueChange={field.onChange}
              aria-invalid={ariaInvalid}
            />
          );
        }
        case 'static-multi': {
          const { mode: _, onValueChange: __, ...rest } = modeProps;
          return (
            <StaticMultiCombobox
              {...rest}
              id={identifier}
              value={(field.value as T[]) ?? []}
              onValueChange={field.onChange}
              aria-invalid={ariaInvalid}
            />
          );
        }
        case 'paginated': {
          const { mode: _, onValueChange: __, ...rest } = modeProps;
          return (
            <PaginatedCombobox
              {...rest}
              id={identifier}
              value={(field.value as T | null) ?? null}
              onValueChange={field.onChange}
              aria-invalid={ariaInvalid}
            />
          );
        }
        case 'paginated-multi': {
          const { mode: _, onValueChange: __, ...rest } = modeProps;
          return (
            <PaginatedMultiCombobox
              {...rest}
              id={identifier}
              value={(field.value as T[]) ?? []}
              onValueChange={field.onChange}
              aria-invalid={ariaInvalid}
            />
          );
        }
        default: {
          modeProps satisfies never;
          throw new Error(`Unhandled HfCombobox mode: ${(modeProps as { mode: string }).mode}`);
        }
      }
    })();

    return (
      <div className="flex flex-col">
        <div className="flex items-center">
          {label && <FieldLabel name={identifier} label={label} />}
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
        {body}
        {error?.message && <ErrorLabel>{error.message}</ErrorLabel>}
      </div>
    );
  };

  return (
    <OdinController
      name={name}
      control={control}
      defaultValue={defaultValue}
      onChange={modeProps.onValueChange as ((value: T | T[] | null) => void) | undefined}
      render={render}
    />
  );
};
